<template>
  <div>
    <b-table
      striped
      hover
      responsive
      small
      :items="rows"
      :fields="fields"
      primary-key="key"
    >
      <template #cell(name)="data">
        {{ data.value }}
        <b-badge
          v-if="data.item.is_freelancer"
          variant="light-secondary"
          class="ml-50"
        >
          {{ $t('staffHours.freelancer') }}
        </b-badge>
        <b-badge
          v-else-if="!data.item.is_payroll"
          variant="light-warning"
          class="ml-50"
        >
          {{ $t('staffHours.noPersonnelNumber') }}
        </b-badge>
      </template>
      <template #cell(timemoto_minutes)="data">
        {{ hm(data.value) }}
      </template>
      <template #cell(hubstaff_minutes)="data">
        <span :id="`hs-${data.item.key}`">{{ hm(data.value) }}</span>
        <b-tooltip
          :target="`hs-${data.item.key}`"
          triggers="hover"
        >
          {{ $t('staffHours.manualPart') }}: {{ hm(data.item.hubstaff_manual_minutes) }}
        </b-tooltip>
      </template>
      <template #cell(worked_minutes)="data">
        <strong>{{ hm(data.value) }}</strong>
      </template>
      <template #cell(paid_absence_minutes)="data">
        {{ hm(data.value) }}
      </template>
      <template #cell(corrected_minutes)="data">
        {{ hm(data.value) }}
      </template>
      <template
        v-for="key in overtimeCols"
        #[`cell(${key})`]="data"
      >
        <span
          :key="key"
          :class="{ 'text-danger': data.value < 0 }"
        >{{ data.value === null ? '–' : formatHm(data.value) }}</span>
      </template>
      <template
        v-for="col in dateCols"
        #[`cell(${col.key})`]="data"
      >
        <span
          v-if="!data.value.length"
          :key="col.key"
        >–</span>
        <span
          v-else
          :id="`${col.key}-${data.item.key}`"
          :key="col.key"
          class="text-primary cursor-pointer"
        >
          {{ data.value.length }}
          <b-popover
            :target="`${col.key}-${data.item.key}`"
            triggers="hover focus"
            :title="col.label"
          >
            <div
              v-for="d in data.value"
              :key="d.date"
            >
              {{ shortDate(d.date) }}
              <b-badge
                v-if="d.source === 'iq'"
                variant="light-info"
              >IQ</b-badge>
              <b-badge
                v-else-if="d.source === 'calendar'"
                variant="light-secondary"
              >{{ $t('staffHours.calendarNrw') }}</b-badge>
            </div>
          </b-popover>
        </span>
      </template>
      <template #cell(cost)="data">
        <span v-if="data.item.cost_basis === 'none'">–</span>
        <span v-else>
          {{ eur(data.value) }}
          <small
            v-if="Number(data.item.employer_factor) !== 1"
            class="text-muted"
            :title="$t('staffHours.employerFactor')"
          >×{{ Number(data.item.employer_factor) }}</small>
          <small
            v-if="data.item.cost_basis === 'salary'"
            class="text-muted d-block"
          >{{ $t('staffHours.fixedSalary') }}</small>
        </span>
      </template>
      <template #cell(warning_count)="data">
        <b-badge
          v-if="data.value"
          variant="light-warning"
          class="cursor-pointer"
          @click="$emit('show-warnings', data.item.key)"
        >
          {{ data.value }}
        </b-badge>
      </template>
    </b-table>
    <small class="text-muted d-block px-1 pb-1">
      {{ $t('staffHours.note') }}
    </small>
  </div>
</template>

<script>
import {
  BBadge, BPopover, BTable, BTooltip,
} from 'bootstrap-vue'
import { formatHm } from '@/utils/hoursMinutes'

export default {
  components: {
    BBadge, BPopover, BTable, BTooltip,
  },
  props: { rows: { type: Array, required: true } },
  data() {
    return { overtimeCols: ['overtime_old_minutes', 'overtime_new_minutes'] }
  },
  computed: {
    dateCols() {
      return [
        { key: 'vacation_dates', label: this.$t('staffHours.cols.vacation') },
        { key: 'holiday_dates', label: this.$t('staffHours.cols.holidays') },
        { key: 'sick_dates', label: this.$t('staffHours.cols.sick') },
      ]
    },
    fields() {
      const c = k => this.$t(`staffHours.cols.${k}`)
      return [
        { key: 'personnel_number', label: c('personnelNumber') },
        { key: 'name', label: c('name'), sortable: true },
        { key: 'team', label: c('team'), sortable: true },
        { key: 'timemoto_minutes', label: c('timemoto'), class: 'text-right' },
        { key: 'hubstaff_minutes', label: c('hubstaff'), class: 'text-right' },
        { key: 'worked_minutes', label: c('worked'), class: 'text-right' },
        { key: 'paid_absence_minutes', label: c('paidAbsence'), class: 'text-right' },
        { key: 'corrected_minutes', label: c('corrected'), class: 'text-right' },
        { key: 'overtime_old_minutes', label: c('overtimeOld'), class: 'text-right' },
        { key: 'overtime_new_minutes', label: c('overtimeNew'), class: 'text-right' },
        { key: 'vacation_dates', label: c('vacation'), class: 'text-center' },
        { key: 'holiday_dates', label: c('holidays'), class: 'text-center' },
        { key: 'sick_dates', label: c('sick'), class: 'text-center' },
        { key: 'cost', label: c('cost'), class: 'text-right' },
        { key: 'warning_count', label: '', class: 'text-center' },
      ]
    },
  },
  methods: {
    formatHm,
    hm(minutes) {
      const m = Number(minutes) || 0
      return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`
    },
    eur(v) {
      return Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    shortDate(iso) {
      const [, m, d] = iso.split('-')
      return `${d}.${m}.`
    },
  },
}
</script>
